import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
// Asset imports (keep existing ones)
import ETL_Pipeline_Diagram from "../../Assets/DA/ETL_Pipeline_Diagram.png";

// Icon imports (keep existing ones)
import { PiGraphThin } from "react-icons/pi";
import { LuCloudCog } from "react-icons/lu";
import { SiLooker } from "react-icons/si";

// React Flow imports (keep existing ones)
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
} from "react-flow-renderer";

// Other UI component imports (keep necessary ones)
// import { Img } from "react-image"; // Removed as not used in the final JSX for DS.jsx
import Skeleton from '@mui/material/Skeleton';
import { DataGrid } from "@mui/x-data-grid";
import Iframe from "react-iframe";

// Local component/CSS imports (keep existing ones)
import Sidebar from "./Sidebar"; // For DAG Builder
import "./dag-css.css"; // For DAG Builder

import axios from "axios"; // Keep for API calls

// Constants for icon colors (from the first example)
const ACTIVE_ICON_COLOR = "gray"; // CSS keyword 'gray'
const INACTIVE_ICON_COLOR = "rgb(107, 114, 128)"; // Tailwind's gray-500

// Tab data definition
const tabsData = [
  {
    value: "DAG Builder",
    label: "DAG Builder",
    IconComponent: PiGraphThin,
    description: "Design and visualize data pipelines using a drag-and-drop interface.",
  },
  {
    value: "ETL",
    label: "ETL Pipeline",
    IconComponent: LuCloudCog,
    description: "Explore an example ETL pipeline for Twitter data, tracking top topics in South Africa.",
  },
  {
    value: "Live Dashboard",
    label: "Live Dashboard",
    IconComponent: SiLooker,
    description: "View a live dashboard showcasing data streamed from a cloud data warehouse via Google Looker Studio.",
  },
];

export default function DS() {
  const [activeTabValue, setActiveTabValue] = useState(tabsData[0].value);
  const mainContentRef = useRef(null);

  // Memoize activeTab to avoid re-calculating on every render
  const activeTab = useMemo(() =>
    tabsData.find(tab => tab.value === activeTabValue),
    [activeTabValue]
  );

  // Memoize handleTabChange
  const handleTabChange = useCallback((newValue) => {
    setActiveTabValue(newValue);
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, []);

  // Styling functions (from the first example)
  const getButtonStyles = (buttonValue) => {
    const isActive = activeTabValue === buttonValue;
    let baseClasses =
      "flex items-center p-3 rounded-lg transition-colors duration-150 ease-in-out cursor-pointer w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800";
    
    if (isActive) {
      return `${baseClasses} bg-gray-100 dark:bg-gray-700 border-2 border-gray-500 dark:border-gray-400 focus:ring-gray-500`;
    }
    return `${baseClasses} bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 focus:ring-indigo-500`;
  };

  const getTextColorAndWeightClass = (buttonValue) => {
    const isActive = activeTabValue === buttonValue;
    const textColorClass = isActive ? "text-gray-700 dark:text-gray-200" : "text-gray-700 dark:text-gray-300";
    const fontWeightClass = isActive ? "font-semibold" : "font-medium";
    return `${textColorClass} ${fontWeightClass}`;
  };

  // --- States and Logic for "DAG Builder" Tab ---
  const initialNodes = [{ id: "1", type: "input", data: { label: "Data Source" }, position: { x: 250, y: 5 } }];
  let nodeIdCounter = 0; // Renamed to avoid conflict with 'id' in map
  const getId = () => `dndnode_${nodeIdCounter++}`;
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowWrapper.current || !reactFlowInstance) return; // Guard against null refs
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");
      const label = event.dataTransfer.getData("label");
      if (typeof type === "undefined" || !type) return;
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
      const newNode = { id: getId(), type, position, data: { label: label } };
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes] 
  );

  // --- States and Logic for "ETL" Tab ---
  const [isEtlLoading, setEtlLoading] = useState(false); 
  const [TopTopics, setTopTopics] = useState([
    { Name: "Elon", id: 1 }, { Name: "SARB", id: 2 }, { Name: "Annie", id: 3 }, { Name: "Bokke", id: 4 }, { Name: "Bongi", id: 5 }, { Name: "Bravo", id: 6 }, { Name: "Corny", id: 7 }, { Name: "Italy", id: 8 }, { Name: "Kolbe", id: 9 }, { Name: "Qatar", id: 10 }, { Name: "Trump", id: 11 }, { Name: "Ayanda", id: 12 }, { Name: "Dj Hlo", id: 13 }, { Name: "Martha", id: 14 }, { Name: "Ntsiki", id: 15 }, { Name: "Willie", id: 16 }, { Name: "Stage 4", id: 17 }, { Name: "#ITAvRSA", id: 18 }, { Name: "#ssrugby", id: 19 }, { Name: "Charlize", id: 20 }, { Name: "Italians", id: 21 }, { Name: "Mastodon", id: 22 }, { Name: "ROYAL AM", id: 23 }, { Name: "Willemse", id: 24 }, { Name: "Afrikaans", id: 25 }, { Name: "Budweiser", id: 26 }, { Name: "Master KG", id: 27 }, { Name: "Rick Ross", id: 28 }, { Name: "sabc plus", id: 29 }, { Name: "shop l10b", id: 30 }, { Name: "#RHODurban", id: 31 }, { Name: "SHEGGZ OLU", id: 32 }, { Name: "Standerton", id: 33 }, { Name: "#Springboks", id: 34 }, { Name: "Manie Libbok", id: 35 }, { Name: "Ras Dumisani", id: 36 }, { Name: "#WooliesBraai", id: 37 }, { Name: "Julius Malema", id: 38 }, { Name: "The Peripheral", id: 39 }, { Name: "Viva Argentina", id: 40 }, { Name: "National Anthem", id: 41 }, { Name: "Gianni Infantino", id: 42 }, { Name: "Hopefully Neymar", id: 43 }, { Name: "Kurt-Lee Arendse", id: 44 }, { Name: "YOU KNOW WHAT YEAH", id: 45 }, { Name: "BELLA X BEAUTIFULBODY", id: 46 }, { Name: "army team south africa", id: 47 }, { Name: "eastgate shopping centre", id: 48 }, { Name: "absa business evolve account", id: 49 }, { Name: "CONGRATULATIONS BEAUTY TUKURA", id: 50 }, { Name: "Blom", id: 51 }, { Name: "Guns", id: 52 }, { Name: "Nota", id: 53 }, { Name: "Oupa", id: 54 }, { Name: "Eskom", id: 55 }, { Name: "Messi", id: 56 }, { Name: "Shape", id: 57 }, { Name: "Sjava", id: 58 }, { Name: "Thami", id: 59 }, { Name: "Top G", id: 60 }, { Name: "Giroud", id: 61 }, { Name: "Libbok", id: 62 }, { Name: "London", id: 63 }, { Name: "Neymar", id: 64 }, { Name: "Ayepyep", id: 65 }, { Name: "Benzema", id: 66 }, { Name: "Cassper", id: 67 }, { Name: "Ecuador", id: 68 }, { Name: "England", id: 69 }, { Name: "Mapimpi", id: 70 }, { Name: "Martial", id: 71 }, { Name: "Pitbull", id: 72 }, { Name: "Route 2", id: 73 }, { Name: "Stage 5", id: 74 }, { Name: "Uruguay", id: 75 }, { Name: "Number 2", id: 76 }, { Name: "Woodmead", id: 77 }, { Name: "6 Nations", id: 78 }, { Name: "Argentina", id: 79 }, { Name: "Lady Zamar", id: 80 }, { Name: "#CastleLite", id: 81 }, { Name: "Pearl Thusi", id: 82 }, { Name: "#KeLiteStyle", id: 83 }, { Name: "Ashi Nthwela", id: 84 }, { Name: "#FIFAWorldCup", id: 85 }, { Name: "#RIPDJSumbody", id: 86 }, { Name: "Louis Vuitton", id: 87 }, { Name: "Arabile Gumede", id: 88 }, { Name: "Blessed Sunday", id: 89 }, { Name: "Monate Mpolaye", id: 90 }, { Name: "Alles Les Bleus", id: 91 }, { Name: "you know what yeah", id: 92 }, { Name: "CONGRATULATIONS BELLA", id: 93 }, { Name: "#JusticeForBafanaSithole", id: 94 }
  ]);

  const get_top_topics = async () => {
    setEtlLoading(true);
    try {
      const res = await axios.get(
        "https://master-twitter-warehouse-api-lqfbwlkw2a-uc.a.run.app/api/v1/project/core/process_request",
      );
      setTopTopics(res.data.data);
    } catch (error) {
      console.error("Failed to fetch top topics:", error);
    } finally {
      setEtlLoading(false);
    }
  };

  useEffect(() => {
    if (activeTabValue === "ETL") { 
        get_top_topics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabValue]); 

  // --- States and Logic for "Live Dashboard" Tab ---
  const [DashLoading, setDashLoading] = useState(true);


  // Configuration for sticky sidebar (from the first example)
  const fixedSidebarTopOffset = "md:top-16"; 
  const sidebarMaxHeight = "md:max-h-[calc(100vh-4rem-2rem)]"; 

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex flex-col">
      <header className="py-6 md:py-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16"> 
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white project-heading">
            Data Engineering Projects
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300 text-base md:text-lg">
            Explore various data engineering tools and pipelines.
          </p>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 md:py-8 gap-8 md:gap-10">
        {/* Sidebar Navigation */}
        <nav
          // Apply Orwell.jsx policy: remove w-full and self-start
          // Retain desktop widths, sticky behavior, and shrink-0
          className={`md:w-72 lg:w-1/4 shrink-0 md:sticky ${fixedSidebarTopOffset} ${sidebarMaxHeight} md:overflow-y-auto`}
          aria-labelledby="project-navigation-title"
        >
          <div className="space-y-3">
            <h2 id="project-navigation-title" className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 hidden md:block">
              Select Project
            </h2>
            {tabsData.map((tab) => {
              const isActive = activeTabValue === tab.value;
              const { IconComponent } = tab;
              return (
                <button
                  key={tab.value}
                  type="button"
                  className={getButtonStyles(tab.value)} // This already includes w-full
                  onClick={() => handleTabChange(tab.value)}
                  aria-pressed={isActive}
                >
                  <IconComponent
                    className="mr-2.5 flex-shrink-0 w-5 h-5" 
                    color={isActive ? ACTIVE_ICON_COLOR : INACTIVE_ICON_COLOR}
                  />
                  <span className={`text-sm ${getTextColorAndWeightClass(tab.value)}`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main Content */}
        <main
          ref={mainContentRef}
          className="flex-1 min-w-0 md:overflow-y-auto" // md:overflow-y-auto for main content scroll on desktop if needed
        >
          {activeTab && (
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{activeTab.label}</h3>
                  {activeTab.description && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{activeTab.description}</p>
                  )}
              </div>
              <div className="p-4 md:p-6 lg:p-8"> 
                
                {activeTab.value === "DAG Builder" && (
                  <div className="space-y-4"> 
                    <p className="text-gray-700 dark:text-gray-300">
                      A good start in Data Engineering is how you design data
                      pipelines. This tool below can be used to visualize a data
                      pipeline, helping you to translate that flow into an
                      orchestration tool like Apache Airflow or any of its derivatives.
                    </p>
                    <div className="dndflow" style={{ height: '600px' }}> 
                      <ReactFlowProvider>
                        <div className="reactflow-wrapper" ref={reactFlowWrapper} style={{ height: '100%' }}>
                          <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onInit={setReactFlowInstance}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            fitView
                          >
                            <Controls />
                          </ReactFlow>
                        </div>
                        <Sidebar />
                      </ReactFlowProvider>
                    </div>
                  </div>
                )}

                {activeTab.value === "ETL" && (
                  <div className="space-y-6">
                    <div>
                      <p className="text-gray-700 dark:text-gray-300 mb-2">
                        ETL pipelines handle incoming data in a scalable way, allowing
                        data to reach an endpoint reliably. Below is an example using
                        Twitter's API as the data source, creating a pipeline that tracks
                        the top twitter topics within and around the five major
                        population centers in South Africa, with data streamed in daily.
                      </p>
                      <p className="text-gray-700 dark:text-gray-300 mb-2">
                        This is an example of a batch data pipeline. We could turn this
                        into a streaming pipeline by adding a pub/sub step to write into
                        the raw table rather than reading straight from the cloud
                        storage bucket.
                      </p>
                      <p className="text-sm text-red-500 dark:text-red-400">
                        Please note: This tool may stop working in the future due to Twitter restricting free API access.
                      </p>
                    </div>
                    
                    <div className="flex justify-center my-4">
                        <img /* Changed from Img to img for simplicity as no specific react-image features used here */
                        src={ETL_Pipeline_Diagram}
                        alt="ETL Pipeline Diagram"
                        className="max-w-xs md:max-w-sm lg:max-w-md rounded-md shadow-md" 
                        />
                    </div>

                    {isEtlLoading && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Top Topics Today</h4>
                        <Skeleton sx={{ height: 400, bgcolor: 'grey.700' }} animation="wave" variant="rectangular" />
                      </div>
                    )}

                    {!isEtlLoading && TopTopics && TopTopics.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Top Topics Today</h4>
                        <div style={{ height: 450, width: "100%" }} className="bg-white dark:bg-gray-700 rounded">
                          <DataGrid
                            rows={TopTopics}
                            columns={[
                              { field: "id", headerName: "Rank", width: 90 },
                              { field: "Name", headerName: "Topic", flex: 1 },
                            ]}
                            pageSizeOptions={[10, 20, 50]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            sx={{
                                border: '1px solid rgba(229, 231, 235, 1)', 
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'rgba(243, 244, 246, 1)', 
                                    color: 'rgba(17, 24, 39,1)', 
                                },
                                '& .MuiDataGrid-cell': {
                                    color: 'rgba(55, 65, 81,1)', 
                                },
                                // Dark mode specific styles for DataGrid if needed
                                // This requires your Tailwind dark mode class (e.g., 'dark') to be on a parent (html or body)
                                // And then using MUI's ability to target based on parent class or a theme provider.
                                // For simplicity, the above styles are light-mode focused.
                                // For Tailwind dark mode integration with MUI, you might need to conditionally apply sx
                                // or use MUI's ThemeProvider and useDarkMediaQuery hook.
                            }}
                          />
                        </div>
                      </div>
                    )}
                     {!isEtlLoading && (!TopTopics || TopTopics.length === 0) && (
                        <p className="text-center text-gray-500 dark:text-gray-400 py-4">No topics data to display.</p>
                     )}
                  </div>
                )}

                {activeTab.value === "Live Dashboard" && (
                  <div className="space-y-4">
                    <p className="text-gray-700 dark:text-gray-300">
                      The last step in Data Engineering is showcasing your data in a
                      useful way. Here I have used Google's Looker Studio platform to
                      showcase data that is being live streamed from my cloud data warehouse.
                    </p>
                    
                    {DashLoading &&
                        <Skeleton sx={{ height: 680, bgcolor: 'grey.700' }} animation="wave" variant="rectangular" className="w-full rounded-md" />
                    }
                    <div style={{ display: DashLoading ? 'none' : 'block' }} className="w-full h-[680px] border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                        <Iframe
                        url="https://datastudio.google.com/embed/reporting/6f0df7a2-566d-4d7c-9fc5-c88f8b20ccd0/page/p_4k8r7xri0c"
                        width="100%"
                        height="100%" 
                        frameBorder="0"
                        allowFullScreen
                        onLoad={()=>setDashLoading(false)}
                        />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}